import { useMsal } from "@azure/msal-react";
import { BuildUrl } from "../helpers/ApiHelpers";
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertType, OursageArticle, OursageBlogPage } from "../models";
import { useHttp } from "../hooks";
import { format } from "date-fns/format";
import { Box, Modal, Pagination, Stack } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { LightTooltip } from "../components/Common";
import { useNavigate } from "react-router-dom";
import { useAlertContext, useUserContext } from "../contexts";
import {
	ADMIN,
	EXECUTIVE_CENTRE_ADMIN,
	MARKETING,
	apiAccessRequest
} from "../constants";

const pageSize = 20;

export const OurSageBlogs = () => {
	const navigate = useNavigate();
	const oursageUrl = BuildUrl("OurSage/");
	const getUrl = `${oursageUrl}articles`;
	const queryString = (page: number) =>
		`enabledOnly=false&count=${pageSize}&offset=${(page - 1) * pageSize}`;
	const [pageNumber, setPageNumber] = useState(1);

	const { data, isLoading, isError, fetchData } = useHttp<OursageBlogPage>(
		getUrl,
		{
			method: "GET",
			queryString: queryString(pageNumber),
			autoFetch: true
		}
	);
	const [isRefreshRequired, setIsRefreshRequired] = useState(false);
	const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
	const deleteArticle = useRef<OursageArticle | null>(null);
	const { showAlert, clearAlert } = useAlertContext();
	const { user, account } = useUserContext();
	const { instance } = useMsal();
	const hasEditPermission =
		user?.roles.includes(ADMIN) ||
		user?.roles.includes(MARKETING) ||
		user?.roles.includes(EXECUTIVE_CENTRE_ADMIN) ||
		false;

	const retrieveArticles = useCallback(async () => {
		if (isRefreshRequired) {
			await fetchData();
			setIsRefreshRequired(false);
		}
	}, [isRefreshRequired, fetchData]);

	useEffect(() => {
		retrieveArticles();
	}, [retrieveArticles]);

	useEffect(() => {
		isError
			? showAlert({
					message: "Error retrieving blogs",
					alertType: AlertType.error
				})
			: clearAlert();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isError]);

	useEffect(() => {
		!hasEditPermission
			? showAlert({
					message: "You do not have permission to update blogs",
					alertType: AlertType.info,
					timeout: true
				})
			: clearAlert();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasEditPermission]);

	const handleChangePage = (
		event: React.ChangeEvent<unknown>,
		page: number
	) => {
		setPageNumber(page);
	};
	const handleEditClick = useCallback(
		(article: OursageArticle) => {
			if (!hasEditPermission) {
				return;
			}
			navigate("/oursageBlogEdit", { state: { article } });
		},
		[hasEditPermission, navigate]
	);

	const handleDeleteClick = useCallback(
		(article: OursageArticle) => {
			if (!hasEditPermission) {
				return;
			}
			deleteArticle.current = article;
			setDisplayDeleteModal(true);
		},
		[hasEditPermission]
	);

	const handleDeleteResponse = useCallback(
		async (response: boolean) => {
			if (response) {
				try {
					const tokenResponse = await instance.acquireTokenSilent({
						account,
						scopes: apiAccessRequest.scopes
					});
					const response = await fetch(
						`${oursageUrl}article/${deleteArticle.current?.id}`,
						{
							method: "DELETE",
							headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
						}
					);

					if (response.ok) {
						setIsRefreshRequired(true);
					} else {
						throw new Error("Delete article failed");
					}
				} catch (e) {
					console.info("Delete article failed", e);
				}
			}
			setDisplayDeleteModal(false);
			deleteArticle.current = null;
		},
		[oursageUrl, account, instance]
	);

	const renderTableRow = (article: OursageArticle) => {
		return (
			<tr key={article.id}>
				<td>{article.title}</td>
				<td align="left" style={{ display: "flex" }}>
					<img
						src={article.imageUrl}
						alt={article.title}
						style={{ maxWidth: 180 }}
					/>
				</td>
				<td align="left">
					<div style={{ display: "flex", gap: 10 }}>
						<a
							href={article.articleUrl}
							target="_blank"
							rel="noreferrer"
							style={{ color: "#1565c0" }}
						>
							<OpenInNewIcon />
						</a>
					</div>
				</td>
				<td align="center">
					{article.enabled && <CheckOutlinedIcon style={{ color: "green" }} />}
					{!article.enabled && <CloseOutlinedIcon style={{ color: "red" }} />}
				</td>
				<td align="center">{article.displayOrder}</td>
				<td align="left">{format(article.updated, "dd/MM/yyyy HH:mm")}</td>
				<td align="left">{renderTooltips(article)}</td>
			</tr>
		);
	};

	const renderTooltips = (article: OursageArticle) => {
		return (
			<>
				{hasEditPermission ? (
					<LightTooltip title="Delete">
						<DeleteOutlineIcon
							className="icon"
							style={{ marginRight: 10 }}
							onClick={() => {
								handleDeleteClick(article);
							}}
						/>
					</LightTooltip>
				) : (
					<DeleteOutlineIcon
						className="icon-disabled"
						style={{ marginRight: 10 }}
					/>
				)}

				{hasEditPermission ? (
					<LightTooltip title="Edit">
						<EditOutlinedIcon
							className="icon"
							onClick={() => {
								handleEditClick(article);
							}}
						/>
					</LightTooltip>
				) : (
					<EditOutlinedIcon
						className="icon-disabled"
						onClick={() => {
							handleEditClick(article);
						}}
					/>
				)}
			</>
		);
	};

	const renderDeleteConfirmationModal = () => {
		return (
			<Modal
				open={displayDeleteModal}
				onClose={() => handleDeleteResponse(false)}
				aria-labelledby="parent-modal-title"
				aria-describedby="parent-modal-description"
			>
				<Box className="modal" sx={{ width: 150 }}>
					<h2 id="parent-modal-title">
						Delete '{deleteArticle.current?.title || ""}'
					</h2>
					<p id="parent-modal-description" className="pb-4">
						Are you sure you want to delete article?
					</p>
					<div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
						<button
							className="ch-button ch-button-primary"
							onClick={() => handleDeleteResponse(false)}
						>
							No
						</button>
						<button
							className="ch-button ch-button-secondary"
							onClick={() => handleDeleteResponse(true)}
						>
							Yes
						</button>
					</div>
				</Box>
			</Modal>
		);
	};

	if (isLoading) {
		return (
			<Box className="icon-loading">
				<CircularProgress size={70} />
			</Box>
		);
	} else if (isError) {
		return <></>;
	} else {
		return (
			<>
				<div className="ch-l-text my-4">
					Our Sage Blogs
					{hasEditPermission ? (
						<LightTooltip title="Add">
							<AddCircleOutlineOutlinedIcon
								className="icon"
								style={{
									marginLeft: 20,
									fontSize: 32,
									verticalAlign: "text-top"
								}}
								onClick={() => {
									handleEditClick({ id: 0 } as OursageArticle);
								}}
							/>
						</LightTooltip>
					) : (
						<AddCircleOutlineOutlinedIcon
							className="icon-disabled"
							style={{
								marginLeft: 20,
								fontSize: "1.1em",
								verticalAlign: "text-top"
							}}
						/>
					)}
				</div>
				<table
					aria-label="our sage artiles"
					className="ch-table"
					style={{ minWidth: 650 }}
				>
					<thead>
						<tr>
							<th>Title</th>
							<th>Image</th>
							<th>Article Url</th>
							<th>Display in App</th>
							<th>Display order</th>
							<th>Updated</th>
							<th />
						</tr>
					</thead>
					{data?.articles && (
						<tbody>
							{data?.articles.map((article) => renderTableRow(article))}
						</tbody>
					)}
				</table>
				<Stack alignItems="center" style={{ marginTop: 10 }}>
					<Pagination
						shape="rounded"
						count={data?.totalPages || 0}
						page={pageNumber}
						onChange={handleChangePage}
					/>
				</Stack>
				{renderDeleteConfirmationModal()}
			</>
		);
	}
};
